/**
 * /shop/ AJAX filter driver.
 *
 * The brain of the live-filter UX. Sibling components (shop-filter-form,
 * shop-price-range, shop-filter-search) only translate user actions
 * into a single `shop:filter-change` event on the filter form — this
 * driver owns everything else:
 *
 *   • Reads the form's current state, builds a /shop/?… URL.
 *   • history.pushState's that URL so the address bar / share / bookmark
 *     all reflect the live filter.
 *   • Fetches /wp-json/tx-sensors/v1/shop?… (same params).
 *   • Swaps the grid + pager fragments in place, updates every
 *     `data-shop-count="…"` span and the toolbar count.
 *   • Cancels any in-flight fetch (AbortController) so the latest
 *     filter click always wins.
 *   • popstate (back / forward) replays the URL by syncing form
 *     inputs from it, then fetching — no full reload.
 *
 * Pager clicks are intercepted via delegation on the catalog body
 * (pager HTML is replaced on every refresh, so per-link listeners
 * would go stale). Pager hrefs are public catalog URLs built by the
 * server for the surface the page is on; a /wp-json/ URL is never
 * pushed into history.
 *
 * Initial-load and AJAX-refresh produce byte-identical fragments
 * (the REST endpoint uses the same template partials as the SSR
 * page), so cards never visually flicker on swap-in.
 *
 * No-JS fallback: the form keeps method="get" and an action URL, so
 * if JS is off, browser-default form submission still loads /shop/?…
 * and the SSR template renders the same view.
 */
const init = () => {
    const app = document.querySelector('[data-shop-app]');
    if (!app) return;

    const endpoint = app.dataset.shopRest;
    if (!endpoint) return;

    const form        = app.querySelector('[data-shop-filter-form]');
    const grid        = app.querySelector('[data-shop-grid]');
    const pager       = app.querySelector('[data-shop-pager]');
    const body        = app.querySelector('[data-shop-catalog-body]');
    const exactMatch  = app.querySelector('[data-shop-exact-match]');
    const filterCount = app.querySelector('[data-shop-filter-count]');
    const toolCount   = app.querySelector('[data-shop-toolbar-count]');
    if (!form || !grid || !pager || !body) return;

    const minInput     = form.querySelector('[data-shop-price-min]');
    const maxInput     = form.querySelector('[data-shop-price-max]');
    const priceWrap    = form.querySelector('[data-shop-price-range]');
    const priceFloor   = priceWrap ? parseInt(priceWrap.dataset.priceFloor,   10) : 0;
    const priceCeiling = priceWrap ? parseInt(priceWrap.dataset.priceCeiling, 10) : 0;

    // Archive context — set by page-shop.php when serving a
    // /product-category/{slug}/, /product-tag/{slug}/ or
    // /trademark/{slug}/ URL:
    //   • archiveAxis  = the ShopFilter param the term pre-seeds
    //                    ('cat', 'ptag' or 'brand')
    //   • archiveSlug  = the implicit term slug for this page
    //   • archiveUrl   = the canonical archive path
    // urlFromForm uses these to (a) strip the implicit axis entry
    // from pushState URLs (since the archive path already implies
    // it), (b) build URLs that stay on the archive route while the
    // term is represented in the form (cat = rail checkbox, tag =
    // hidden input), and (c) fall back to /shop/ when the user
    // unchecks it. restParams re-injects the param for the REST
    // call so the server returns scoped results.
    // A TSC Product Grid in full catalog mode: its signed sidebar list
    // and column count ride along with every REST call; with its views
    // switched off (no toggle) the catalog stays the grid whatever
    // ?view= a shared link carries.
    const railToken = app.dataset.shopRail || '';
    const gridCols  = app.dataset.shopCols || '';
    const hasViews  = !!app.querySelector('[data-shop-view-btn]');

    const archiveAxis  = app.dataset.shopArchiveAxis || 'cat';
    const archiveSlug  = app.dataset.shopArchiveSlug || '';
    const archiveUrl   = app.dataset.shopArchiveUrl || '';
    const archiveParam = archiveAxis + '[]';

    const restRoot = new URL(endpoint, location.origin).pathname.replace(/tx-sensors\/v1\/shop\/?$/, '');
    const isRestUrl = (url) => url.pathname.startsWith(restRoot) || url.searchParams.has('rest_route');

    // WP's canonical redirect moves ?paged=N into the path (/page/N/),
    // so a landing or popstate URL can carry the page there.
    const PAGE_PATH = /^(.*\/)page\/(\d+)\/?$/;
    const catalogPath = (url) => {
        const m = url.pathname.match(PAGE_PATH);
        return m ? m[1] : url.pathname;
    };

    // A param or control name as one key: "cat[2]" and "cat[]" → "cat[]",
    // "attr[slug][0]" → "attr[slug][]", percent-escapes decoded (a
    // server-built URL carries an encoded attribute slug decoded).
    const safeDecode = (s) => {
        try {
            return decodeURIComponent(s);
        } catch {
            return s;
        }
    };
    const controlKey = (name) => {
        const attr = /^attr\[([^\]]*)\](?:\[\d*\])?$/.exec(name);
        return safeDecode(attr ? `attr[${attr[1]}][]` : name.replace(/\[\d*\]$/, '[]'));
    };

    // The params of the built-in groups — the ones the server rail shows
    // while in play even when Theme Options → Shop filters leaves them
    // out. (Pressure and attribute params of an omitted group are ignored
    // server-side; the URL's other params need no control.)
    const FILTER_PARAMS = new Set(['q', 's', 'in_stock', 'ships_today', 'cat', 'brand', 'ptag', 'thread', 'ip', 'material', 'price_min', 'price_max']);
    const ALIASES = { s: 'q', ships_today: 'in_stock' };

    /**
     * Whether the rail has a control for every built-in filter a URL
     * selects. A saved set or an older link may select on a group Theme
     * Options leaves out: the server rail shows such a group while it's
     * in play, but an AJAX swap can't add one, and the next change would
     * drop the filter unseen — so those URLs load in full. The archive's
     * hidden scope input counts for its own term only.
     */
    const railShows = (url) => {
        const names = new Set([...form.elements]
            .filter((el) => el.name && el.type !== 'hidden')
            .map((el) => controlKey(el.name)));
        for (const [key, value] of url.searchParams) {
            if (value === '' || !FILTER_PARAMS.has(key.replace(/\[.*$/, ''))) continue;
            if (archiveSlug && controlKey(key) === archiveParam && value === archiveSlug) continue;
            const name = controlKey(ALIASES[key] || key);
            if (!names.has(name) && !names.has(name + '[]') && !names.has(name.replace(/\[\]$/, ''))) {
                return false;
            }
        }
        return true;
    };

    let inflight = null;

    /**
     * Walk URLSearchParams and drop price_min / price_max when
     * they're at the catalog's natural bounds, so the URL stays clean
     * (no ?price_min=3&price_max=2239 noise when the slider hasn't
     * been touched).
     */
    const stripPriceDefaults = (params) => {
        if (params.has('price_min') && parseInt(params.get('price_min'), 10) === priceFloor) {
            params.delete('price_min');
        }
        if (params.has('price_max') && parseInt(params.get('price_max'), 10) === priceCeiling) {
            params.delete('price_max');
        }
    };

    /**
     * Build a /shop/?… URL from the form's current state.
     * resetPaged: drop the `paged` param so a filter change always
     * lands on page 1 of the new result set.
     */
    const urlFromForm = ({ resetPaged = false } = {}) => {
        const fd = new FormData(form);
        const params = new URLSearchParams();
        for (const [k, v] of fd.entries()) {
            if (v === '' || v == null) continue;
            params.append(k, v);
        }
        // Toolbar selects (per_page, sort) live OUTSIDE the form
        // DOM tree, linked via the HTML5 `form="<id>"` attribute.
        // Per spec FormData(form) should pick them up, but
        // explicitly merging them here removes any browser-specific
        // doubt and is a no-op when FormData already captured them
        // (params.set overwrites, preserving the single-value
        // semantics of the selects).
        document.querySelectorAll('[data-shop-toolbar-select]').forEach((sel) => {
            if (!sel.name) return;
            // The first option is the server default (syncFormFromUrl
            // falls back to it too); omitting it keeps these URLs the
            // same as the server-built pager links.
            if (sel.value === '' || sel.value === sel.options[0]?.value) {
                params.delete(sel.name);
            } else {
                params.set(sel.name, sel.value);
            }
        });
        // Presentational params that aren't form-owned must survive
        // a filter change. Right now that's ?view= — the view toggle
        // lives in the catalog header, not the filter form, so its
        // value isn't picked up by FormData. Without this carry-over,
        // changing a SHOW / SORT / checkbox would silently drop
        // ?view=rows|table and revert to grid.
        const inheritFromUrl = hasViews ? [ 'view' ] : [];
        const currentParams  = new URL(location.href).searchParams;
        for (const name of inheritFromUrl) {
            if (currentParams.has(name) && !params.has(name)) {
                params.set(name, currentParams.get(name));
            }
        }

        if (resetPaged) params.delete('paged');
        stripPriceDefaults(params);

        // Archive-context URL build:
        //   - archive term still in form → stay on archive URL,
        //     strip the implicit axis entry from the query string
        //   - archive term unchecked      → fall back to /shop/
        //     with whatever other filters remain
        let base = form.getAttribute('action') || location.pathname;
        if (archiveSlug && archiveUrl) {
            const list = params.getAll(archiveParam);
            if (list.includes(archiveSlug)) {
                params.delete(archiveParam);
                for (const v of list) {
                    if (v !== archiveSlug) params.append(archiveParam, v);
                }
                base = archiveUrl;
            }
            // else: form.action (= /shop/) is the right base.
        }

        const qs = params.toString();
        return new URL(base + (qs ? '?' + qs : ''), location.origin);
    };

    /**
     * Guard for the address bar: a REST URL (e.g. a pager link from a
     * cached pre-fix REST response) is rebuilt from the form state,
     * keeping its page number.
     */
    const publicUrl = (url) => {
        if (!isRestUrl(url)) return url;
        const target = urlFromForm({ resetPaged: true });
        const page = parseInt(url.searchParams.get('paged'), 10);
        if (page > 1) target.searchParams.set('paged', String(page));
        return target;
    };

    /**
     * REST query for a public catalog URL. The endpoint only reads the
     * query string, so this folds in what the URL says elsewhere:
     *   • /page/N/ becomes paged=N
     *   • on the archive path, the implicit term (the pushed URL
     *     strips it) plus the `archive` hint that makes the server
     *     build pager links on the archive path instead of /shop/
     *   • the search route's ?s= becomes q (ShopFilter's param)
     */
    const restParams = (url) => {
        const params = new URLSearchParams(url.searchParams);
        const pageMatch = url.pathname.match(PAGE_PATH);
        if (pageMatch && !params.has('paged')) {
            params.set('paged', pageMatch[2]);
        }
        if (params.has('s')) {
            if (!params.has('q')) params.set('q', params.get('s'));
            params.delete('s');
        }
        if (archiveSlug && archiveUrl && catalogPath(url) === archiveUrl) {
            if (!params.getAll(archiveParam).includes(archiveSlug)) {
                params.append(archiveParam, archiveSlug);
            }
            params.set('archive', `${archiveAxis}:${archiveSlug}`);
        }
        if (railToken) params.set('rail', railToken);
        if (gridCols) params.set('cols', gridCols);
        if (!hasViews) params.delete('view');
        return params;
    };

    /**
     * Reflect URL params back into form inputs. Used by popstate so
     * the back / forward buttons can restore checkbox / search /
     * slider state without firing change events that would loop us
     * back into another fetch.
     */
    const syncFormFromUrl = (url) => {
        const p = url.searchParams;

        const searchInput = form.querySelector('input[name="q"]');
        if (searchInput) searchInput.value = p.get('q') || '';

        // ships_today: the retired "Ships today" filter, which meant in stock.
        const stockCb = form.querySelector('input[type="checkbox"][name="in_stock"]');
        if (stockCb) stockCb.checked = p.has('in_stock') || p.has('ships_today');

        // Server-built links (pager, canonical) index their arrays
        // (brand[0]=…), the form pushes brand[]=…; read both.
        const listParam = (base) => new Set([...p.entries()]
            .filter(([k]) => k === base || k.startsWith(base + '['))
            .map(([, v]) => v));
        const multi = {
            'cat[]':      listParam('cat'),
            'ptag[]':     listParam('ptag'),
            'brand[]':    listParam('brand'),
            'thread[]':   listParam('thread'),
            'ip[]':       listParam('ip'),
            'material[]': listParam('material'),
        };
        // On the archive path its term is implied, not in the query.
        if (archiveSlug && archiveUrl && catalogPath(url) === archiveUrl && multi[archiveParam]) {
            multi[archiveParam].add(archiveSlug);
        }
        for (const [name, values] of Object.entries(multi)) {
            form.querySelectorAll(`input[type="checkbox"][name="${name}"]`).forEach((cb) => {
                cb.checked = values.has(cb.value);
            });
        }

        // Product-attribute groups (Theme Options → Shop filters):
        // attr[<slug>][] checkboxes, matched on decoded keys.
        const attrValues = new Map();
        for (const [k, v] of p.entries()) {
            if (!k.startsWith('attr[')) continue;
            const key = controlKey(k);
            if (!attrValues.has(key)) attrValues.set(key, new Set());
            attrValues.get(key).add(v);
        }
        form.querySelectorAll('input[type="checkbox"][name^="attr["]').forEach((cb) => {
            cb.checked = !!attrValues.get(controlKey(cb.name))?.has(cb.value);
        });

        // Pressure range: the slider's prmin / prmax value inputs, left
        // disabled (out of the URL) when the URL sets no range;
        // shop-pressure-range.js repaints from them on state-restored.
        const prMin = form.querySelector('[data-shop-pressure-min]');
        const prMax = form.querySelector('[data-shop-pressure-max]');
        if (prMin && prMax) {
            const ranged = p.has('prmin') || p.has('prmax');
            prMin.value    = p.get('prmin') || '';
            prMax.value    = p.get('prmax') || '';
            prMin.disabled = !ranged;
            prMax.disabled = !ranged;
        }

        // The archive tag scope rides in a hidden input (the rail has
        // no tag controls). Mirror it from the target URL: when the
        // URL carries neither the archive path nor the scope param
        // (CLEAR ALL, manual /shop/ nav), DISABLE it — disabled
        // inputs are excluded from FormData, so the cleared scope
        // can't resurrect on the next filter change. Re-enable when
        // navigation returns to the archive route (popstate).
        if (archiveSlug && archiveUrl) {
            const hidden = form.querySelector(`input[type="hidden"][name="${archiveParam}"]`);
            if (hidden) {
                const scoped = catalogPath(url) === archiveUrl
                    || multi[archiveParam].has(archiveSlug);
                hidden.disabled = !scoped;
            }
        }

        if (minInput && maxInput) {
            const pmin = p.get('price_min');
            const pmax = p.get('price_max');
            minInput.value = pmin == null ? String(priceFloor)   : String(pmin);
            maxInput.value = pmax == null ? String(priceCeiling) : String(pmax);
        }

        // Toolbar selects (per_page, sort) — restore from URL or
        // fall back to the element's first <option>. Required for
        // CLEAR ALL + popstate not to leave the controls visually
        // out of sync with the active filter state.
        document.querySelectorAll('[data-shop-toolbar-select]').forEach((sel) => {
            if (!sel.name) return;
            const v = p.get(sel.name);
            sel.value = v != null ? v : (sel.options[0] ? sel.options[0].value : '');
        });

        // Carry the target URL on the event so listeners don't have
        // to read window.location.href (which on shop:navigate
        // events hasn't been pushState'd yet, leading to stale-state
        // bugs in downstream toggles).
        document.dispatchEvent(new CustomEvent('shop:state-restored', {
            detail: { url },
        }));
    };

    /**
     * Update every filter-state-dependent count from the server
     * response. Selector contract — every count span has
     * `data-shop-count="<axis>:<slug>"` (or "stock" for the lone
     * in-stock counter).
     */
    const renderCounts = (data) => {
        if (filterCount) {
            filterCount.textContent = `${data.filtered_count} OF ${data.total_products} SKUs`;
        }
        if (toolCount) {
            toolCount.textContent = String(data.filtered_count);
        }
        app.querySelectorAll('[data-shop-count]').forEach((el) => {
            const key = el.dataset.shopCount;
            if (key === 'stock') {
                el.textContent = String(data.in_stock_count);
            } else if (key.startsWith('cat:')) {
                const slug = key.slice(4);
                if (data.cat_counts && slug in data.cat_counts) {
                    el.textContent = String(data.cat_counts[slug]);
                }
            } else if (key.startsWith('brand:')) {
                const slug = key.slice(6);
                if (data.brand_counts && slug in data.brand_counts) {
                    el.textContent = String(data.brand_counts[slug]);
                }
            } else if (key.startsWith('thread:')) {
                const value = key.slice(7);
                if (data.thread_counts && value in data.thread_counts) {
                    el.textContent = String(data.thread_counts[value]);
                }
            } else if (key.startsWith('ip:')) {
                const value = key.slice(3);
                if (data.ip_counts && value in data.ip_counts) {
                    el.textContent = String(data.ip_counts[value]);
                }
            } else if (key.startsWith('material:')) {
                const slug = key.slice(9);
                if (data.material_counts && slug in data.material_counts) {
                    el.textContent = String(data.material_counts[slug]);
                }
            } else if (key.startsWith('ptag:')) {
                const slug = key.slice(5);
                if (data.tag_counts && slug in data.tag_counts) {
                    el.textContent = String(data.tag_counts[slug]);
                }
            } else if (key.startsWith('attr:')) {
                // attr:<slug>:<value> — the value may itself hold colons.
                // Values are free text: own keys only ("constructor" is a
                // value, not Object's).
                const own  = (o, k) => !!o && Object.prototype.hasOwnProperty.call(o, k);
                const rest = key.slice(5);
                const sep  = rest.indexOf(':');
                const slug = rest.slice(0, sep);
                const value = rest.slice(sep + 1);
                if (sep > 0 && own(data.attr_counts, slug) && own(data.attr_counts[slug], value)) {
                    el.textContent = String(data.attr_counts[slug][value]);
                }
            } else if (key.startsWith('prpreset:')) {
                const n = (data.pressure_preset_counts || [])[Number(key.slice(9))];
                if (n != null) {
                    el.textContent = String(n);
                    // An empty quick range can't be picked (a pressed one stays usable).
                    const button = el.closest('[data-shop-pressure-preset]');
                    if (button && document.activeElement !== button) {
                        button.disabled = n === 0 && button.getAttribute('aria-pressed') !== 'true';
                    }
                }
            }
        });
    };

    /**
     * Fetch the REST endpoint and swap the grid + pager + counts.
     * Aborts any earlier in-flight request so rapid filter clicks
     * always render the latest state.
     */
    const fetchAndRender = async (searchParams) => {
        if (inflight) inflight.abort();
        const ac = new AbortController();
        inflight = ac;

        body.classList.add('is-loading');

        try {
            const res = await fetch(`${endpoint}?${searchParams.toString()}`, {
                signal: ac.signal,
                headers: { 'Accept': 'application/json' },
                credentials: 'same-origin',
            });
            if (ac.signal.aborted) return;
            if (!res.ok) {
                body.classList.remove('is-loading');
                inflight = null;
                return;
            }
            const data = await res.json();
            if (ac.signal.aborted) return;

            grid.innerHTML  = data.grid_html  || '';
            pager.innerHTML = data.pager_html || '';
            if (exactMatch) {
                exactMatch.innerHTML = data.exact_match_html || '';
            }
            renderCounts(data);
        } catch (err) {
            if (err.name !== 'AbortError') {
                // Fail open: leave the existing grid in place.
                // eslint-disable-next-line no-console
                console.warn('[shop-ajax] fetch failed', err);
            }
        } finally {
            if (inflight === ac) {
                body.classList.remove('is-loading');
                inflight = null;
            }
        }
    };

    /**
     * Keep the "DOWNLOAD LINE SHEET" link in sync with the active
     * filter URL. The PDF endpoint reads the same params as the shop
     * endpoint, archive term and search included, so it gets the
     * same scoped query. Called after every URL change (navigate +
     * popstate).
     */
    const lineSheetLink = app.querySelector('[data-shop-line-sheet]');
    const lineSheetBase = lineSheetLink?.dataset.lineSheetBase || '';
    const syncLineSheetHref = (url) => {
        if (!lineSheetLink || !lineSheetBase) return;
        const params = restParams(url);
        params.delete('archive');
        params.delete('cols');
        const qs = params.toString();
        lineSheetLink.href = lineSheetBase + (qs ? '?' + qs : '');
    };

    /**
     * Public navigation entry point. Push the URL into history
     * (unless told not to — popstate already updated the URL) and
     * fetch the matching state.
     */
    const navigate = (targetUrl, { push = true } = {}) => {
        const url = publicUrl(new URL(targetUrl, location.origin));
        if (push && url.href !== location.href) {
            history.pushState({ shop: true }, '', url.pathname + url.search);
        }
        syncLineSheetHref(url);
        // Tell sibling components (view-toggle, category-rail, etc.)
        // that the URL just changed so they can re-derive their UI
        // state from it. popstate / shop:navigate already get this
        // for free via syncFormFromUrl; the filter-change → navigate
        // path was the missing dispatch.
        document.dispatchEvent(new CustomEvent('shop:state-restored', { detail: { url } }));
        return fetchAndRender(restParams(url));
    };

    /* ----- Event wiring ----- */

    // Sibling components dispatch this when the user has committed
    // a filter change (checkbox flip, slider release, search submit).
    form.addEventListener('shop:filter-change', () => {
        navigate(urlFromForm({ resetPaged: true }));
    });

    // CLEAR ALL link → reset every form input first (so the rail
    // visually matches the empty filter state immediately), then
    // navigate to the bare /shop/ URL. The native href is /shop/
    // so the no-JS fallback still works as a hard navigation.
    app.querySelector('.tx-shop__filter-clear')?.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        const url = new URL(e.currentTarget.href, location.origin);
        syncFormFromUrl(url);
        navigate(url);
    });

    // Pager links — delegated since the pager HTML is replaced on
    // every refresh. Honor cmd/ctrl/shift/middle-click so the user
    // can still open page N in a new tab.
    body.addEventListener('click', (e) => {
        const link = e.target.closest('[data-shop-pager] a');
        if (!link) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        if (link.target === '_blank') return;
        e.preventDefault();
        navigate(link.href).then(() => {
            const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            body.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
        });
    });

    // Back / Forward can leave the rail out of step with the URL: a load
    // that misses the bfcache gets the browser's restored control state
    // over the server render, and WebKit resets autocomplete="off"
    // controls (the search box) just after a bfcache restore. Read the
    // controls back from the URL.
    window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
            window.setTimeout(() => syncFormFromUrl(new URL(location.href)), 0);
        } else if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') {
            syncFormFromUrl(new URL(location.href));
        }
    });

    // Back / forward. Seed an initial state so the first back press
    // doesn't pop us off the page before our popstate handler runs.
    history.replaceState({ shop: true }, '', location.href);
    window.addEventListener('popstate', (e) => {
        if (e.state && !e.state.shop) return;
        const url = new URL(location.href);
        if (!railShows(url)) {
            location.reload();
            return;
        }
        syncFormFromUrl(url);
        syncLineSheetHref(url);
        fetchAndRender(restParams(url));
    });

    // External callers (e.g. shop-saved-sets clicking an entry,
    // shop-view-toggle, anything else dispatching shop:navigate)
    // ask us to navigate to a specific URL.
    //
    // Crucially: pushState BEFORE syncFormFromUrl. syncFormFromUrl
    // fires shop:state-restored, which downstream listeners (e.g.
    // shop-view-toggle) use to re-derive their active state from
    // location.href. If pushState hadn't run, that read would see
    // the OLD URL and the toggle would snap back to the previous
    // view.
    document.addEventListener('shop:navigate', (e) => {
        const targetUrl = e?.detail?.url;
        if (!targetUrl) return;
        const url = publicUrl(new URL(targetUrl, location.origin));
        if (!railShows(url)) {
            location.assign(url.href);
            return;
        }

        if (url.href !== location.href) {
            history.pushState({ shop: true }, '', url.pathname + url.search);
        }
        syncFormFromUrl(url);
        syncLineSheetHref(url);
        fetchAndRender(restParams(url));
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
